import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Button, Modal, Table } from "antd";
import { useTranslation } from "react-i18next";
import { EditOutlined } from "@ant-design/icons";
import { ModalHeader, MoreMenu } from "datastake-daf/dist/components";
import { getInputLabel, getSectionLabel } from "../../helper";
import { BasicInput } from "../../../ViewTIF/components/input";
import DynamicForm from "../../../../components/DynamicFormTif";
import { camelCaseToTitle } from "../../../../helpers/StringHelper";
import AjaxModal from "./ajaxModal";
import useAjaxModal from "./useAjaxModal";
import i18next from "i18next";
import { useGeneralContext } from "../../../../context/General";
import { getLocales } from "../../../../components/Table/newTable";

export default function Repeatable({
  form = {},
  values,
  fields,
  maxRepeat,
  formData = [],
  onUpdate = () => { },
  remove,
  add,
  k,
  changeLinking = () => { },
}) {
  const { t } = useTranslation();
  const [rowToEdit, setRowToEdit] = useState();
  const [linkingData, setLinkingData] = useState({});
  const [addModalRow, setAddModalRow] = useState(null);
  const { ajaxOptions } = useGeneralContext();
  const { automaticallyLink, dataLink, call, onNewSetValue, prefilledValues } = useMemo(() => form?.meta || {}, [form]);
  const entity = useMemo(() => automaticallyLink?.entity || form?.meta?.dataLinkEntity, [automaticallyLink, form]);
  const isAjaxModal = useMemo(() => automaticallyLink && dataLink && call, [automaticallyLink, dataLink, call]);

  useEffect(() => {
    if (values.linking && values.linking[entity] && values.linking[entity]) {
      setLinkingData(values.linking[entity]);
    }
  }, [entity, values.linking])

  const ajaxModalValues = useAjaxModal({
    formsValue: formData,
    call,
    isAjaxModal,
  })

  const data = useMemo(() =>
    formData.length ? formData.map((f, i) => {
      if (isAjaxModal && typeof f === 'string' && linkingData[f]) {
        const value = linkingData[f];
        return { ...value, key: i };
      }

      if (form?.meta?.ajaxOptionsKey) {
        if (linkingData[f[form?.meta?.ajaxOptionsKey]]) {
          const value = linkingData[f[form?.meta?.ajaxOptionsKey]];
          const resData = { ...f, ...value, key: i };
          if (resData.id && !linkingData[resData.id]) {
            setLinkingData({
              ...linkingData,
              [resData.id]: resData,
            })
          }
          return resData;
        }

        const options = ajaxOptions[form?.meta?.ajaxOptionsKey];

        if (Array.isArray(options)) {
          const value = options.find((v) => v.value === f[form?.meta?.ajaxOptionsKey])
          if (value) {
            const resData = { ...f, ...value, key: i };
            if (resData.id && !linkingData[resData.id]) {
              setLinkingData({
                ...linkingData,
                [resData.id]: resData,
              })
            }
            return resData;
          }
        }
      }

      const resData = { ...f, key: i };
      if (resData.id && !linkingData[resData.id]) {
        setLinkingData({
          ...linkingData,
          [resData.id]: resData,
        })
      }
      return resData;
    }) : [{ key: 1, isEmpty: true }, { key: 2, isEmpty: true }]
    , [isAjaxModal, formData, form, ajaxOptions, linkingData]);

  const preSelected = data.map((d) => d.id).filter((d) => !!d);

  const onCloseModal = useCallback(() => {
    setRowToEdit(null);
    setAddModalRow(false);
  }, []);

  const inputs = useMemo(() =>
    Object.values(ajaxModalValues.form)[0]
      ? Object.values(ajaxModalValues.form)[0]
      : form?.inputs || {}
    , [form, ajaxModalValues.form])

  const mapKey = useCallback((key) => ({
    title: (inputs[key] || {}).label,
    key: key,
    dataIndex: key,
    ellipsis: true,
    render: (v, all) => {
      if (all.isEmpty) {
        return <div className="daf-default-cell md" />;
      }

      if (v) {
        return (
          <BasicInput
            name={key}
            key={key}
            config={inputs[key]}
            valueOnlyString
            data={all}
          />
        );
      }

      if ((inputs[key] || {}).noValue) {
        // console.log(i18next.language)
        return (inputs[key] || {}).noValue[i18next.language]
      }

      return '--';
    },
  }), [inputs]);

  const columns = useMemo(() => [
    ...(Array.isArray(form?.meta?.tableKeys) ? form.meta.tableKeys.map(mapKey) : Object.keys(form?.inputs).map(mapKey)),
    {
      key: 'actions',
      dataIndex: 'actions',
      label: '',
      width: 50,
      render: (_, all) => {
        if (all.isEmpty) {
          return <div className="daf-default-cell md" />;
        }

        if (form?.meta?.allowedActions?.edit) {
          return (
            <div
              onClick={() => {
                if (form?.meta?.ajaxOptionsKey) {
                  const _all = Object.keys(form.inputs)
                    .filter((k) => !(form?.meta?.excludedKeys || []).includes(k))
                    .reduce((val, key) => {
                      val[key] = all[key];
                      return val;
                    }, {});

                  setRowToEdit({
                    ..._all,
                    key: all.key,
                    [form?.meta?.ajaxOptionsKey]: all.id || all.value,
                  })
                  return;
                }

                setRowToEdit(all)
              }}
              className="cursor-pointer flex justify-content-center">
              <EditOutlined />
            </div>
          )
        }

        if (form?.meta?.allowedActions?.remove) {
          return (
            <MoreMenu
              items={[
                {
                  label: t('Remove'),
                  key: "remove",
                  onClick: () => remove(all.key),
                },
              ]}
            />
          );
        }

        return null;
      }
    }
  ], [form, mapKey]);

  const _form = { inputs: form.inputs };

  const _label = !form.repeatable ? getInputLabel(form, values)
    : (!form.hideSectionLabel ? form.label ? getInputLabel(form, values) : camelCaseToTitle(k) : null)

  return (
    <div className={form.repeatable ? "repeatable-section mb-6 pb-3" : ''} style={form?.meta?.style}>
      <div className="mb-4">
        <div className="daf-title with-subtitle">
          <div className="flex flex-row w-100">
            <div className="flex flex-column justify-content-center flex-1">
              {form.subTitle ? (
                <>
                  <h1 className="mb-0">{_label}</h1>
                  <p>{form.subTitle}</p>
                </>
              ) : (
                <h1 className="mb-0">{_label}</h1>
              )}
            </div>
            {form.repeatable && fields.length < maxRepeat
              ? (
                <Button
                  disabled={isAjaxModal ? ajaxModalValues.isFetching : false}
                  onClick={() => {
                    // TODO this should not be like that. We need to have another key / config in order to get only data that we need not checking all inputs because it is saving false info on database.
                    // const _values = Object.keys(inputs).reduce((all, key) => {
                    //   if (values[key]) {
                    //     all[key] = values[key];
                    //   }
                    //   return all;
                    // }, {});

                    // setAddModalRow({ ...(_values || {}), ...(prefilledValues || {}), id: '' });
                    setAddModalRow({ ...(prefilledValues || {}) });
                  }}>
                  {getSectionLabel(form.repeatLabel || i18next.t('Add New'), values)}
                </Button>
              ) : null}
          </div>
        </div>
      </div>

      <div className="daf-table-wrapper no-padding repeatable-form-table no-pagination">
        <Table
          locale={getLocales(t)}
          loading={ajaxModalValues.isFetching}
          pagination={false}
          columns={columns}
          dataSource={data}
          rowKey="key"
        />
      </div>

      <Modal
        open={!!addModalRow || !!rowToEdit}
        footer={null}
        width={650}
        className="max-h-50"
        destroyOnClose
        onCancel={onCloseModal}
        title={(
          <ModalHeader
            title={_label}
            actionButtons={rowToEdit ? [
              {
                label: t('Delete'),
                onClick: () => {
                  if (form?.meta?.allowedActions?.remove) {
                    remove(rowToEdit.key);
                    onCloseModal();
                  }
                },
              }
            ] : null}
          />
        )}
      >
        <div className="repeatable-modal-form">
          {isAjaxModal ? (
            <AjaxModal
              formsValue={formData}
              isFetching={ajaxModalValues.isFetching}
              setIsFetching={ajaxModalValues.setIsFetching}
              rowToEdit={rowToEdit || addModalRow || {}}
              call={call}
              value={rowToEdit}
              onNewSetValue={onNewSetValue}
              onChange={(id, value) => {
                if (rowToEdit) {
                  const { key } = rowToEdit;
                  onUpdate(id, key);
                  changeLinking({ value, linkingKey: id, key: entity })
                  onCloseModal();
                } else {
                  add(id);
                  changeLinking({ value, linkingKey: id, key: entity })
                  onCloseModal();
                }
              }}
              form={ajaxModalValues.form}
              disabledInputs={ajaxModalValues.disabledInputs}
            />
          ) : (
            <DynamicForm
              filterId
              showSaveAndNext={false}
              form={_form}
              excludedKeys={[...(form?.meta?.excludedKeys || [])]}
              data={rowToEdit || addModalRow || {}}
              onCancel={onCloseModal}
              preSelected={preSelected}
              submit={(_payload) => {
                const { form, ...payload } = _payload;

                if (rowToEdit) {
                  const { key, ...rest } = payload;
                  onUpdate(rest, key);
                  onCloseModal();
                } else {
                  add(payload);
                  onCloseModal();
                }
              }}
            />
          )}
        </div>

      </Modal>
    </div>
  )
}
